import React, { useEffect, useRef } from "react";
import { FormatBold, FormatItalic, FormatUnderlined } from "@mui/icons-material";
import { IconButton } from "@mui/material";
import { grey } from "@mui/material/colors"; // A minimal rich-text editor (bold / italic / underline) built on contentEditable — the same
// formatting engine as the Create Discussion composer, minus the opening-reference / file /
// voice features. Emits an HTML string via onChange. Uncontrolled: initialValue seeds the DOM
// once (re-mount it, e.g. by key or tab switch, to re-seed).
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export function RichTextEditor(props) {
    var _props$minHeight;
    const [ref, setRef] = React.useState(null);
    const [selection, setSelection] = React.useState(null);
    const updateTmRef = useRef(null); // seed the contentEditable exactly once: capture the initial HTML on first render so later
    // re-renders (driven by our own onChange) don't re-apply dangerouslySetInnerHTML and reset
    // the DOM / reset the caret / double-process the live content. Re-mount to re-seed.
    const [initialHtml] = React.useState(() => props.initialValue || ""); // track the selection so the B/I/U buttons reflect the caret's current formatting.
    useEffect(() => { if (!ref)
        return; const onSelectionChange = () => { const sel = document.getSelection(); if (sel && sel.anchorNode && ref.contains(sel.anchorNode)) {
        setSelection(sel);
    } }; document.addEventListener("selectionchange", onSelectionChange); return () => document.removeEventListener("selectionchange", onSelectionChange); }, [ref]); // autofocus + place the caret at the end.
    const autoFocusRef = useRef(props.autoFocus);
    useEffect(() => { if (!autoFocusRef.current || !ref)
        return; autoFocusRef.current = false; setTimeout(() => { if (!ref)
        return; ref.focus(); const sel = document.getSelection(); if (!sel)
        return; sel.removeAllRanges(); const r = new Range(); r.selectNodeContents(ref); r.collapse(false); sel.addRange(r); }, 0); }, [ref]); // debounced, matching the composer.
    const emitChange = () => { if (updateTmRef.current)
        clearTimeout(updateTmRef.current); const target = ref; if (!target)
        return; updateTmRef.current = setTimeout(() => { updateTmRef.current = null; props.onChange(target.innerHTML); }, 100); };
    const formatButton = (icon, matcher, query, tag) => /*#__PURE__*/ _jsx(IconButton, { size: "small", color: selectionMatches(ref, selection, matcher) ? "primary" : "default", onClick: () => { toggle(ref, matcher, query, tag); emitChange(); }, children: icon });
    return /*#__PURE__*/ _jsxs("div", { style: { border: "1px solid " + grey["300"], borderRadius: 4, display: "flex", flexDirection: "column", minHeight: (_props$minHeight = props.minHeight) !== null && _props$minHeight !== void 0 ? _props$minHeight : 200 }, children: [/*#__PURE__*/ _jsx("div", { contentEditable: true, ref: setRef, dangerouslySetInnerHTML: { __html: initialHtml }, style: { padding: 8, fontSize: "0.9rem", flex: 1, overflow: "auto" }, onInput: emitChange }), /*#__PURE__*/ _jsxs("div", { style: { backgroundColor: grey["200"], display: "flex", alignItems: "center" }, children: [formatButton(/*#__PURE__*/ _jsx(FormatBold, { fontSize: "inherit" }), isBold, "b,strong", "b"), formatButton(/*#__PURE__*/ _jsx(FormatItalic, { fontSize: "inherit" }), isItalic, "i,em", "i"), formatButton(/*#__PURE__*/ _jsx(FormatUnderlined, { fontSize: "inherit" }), isUnderline, "u", "u")] })] });
}
const isBold = v => v.tagName === "B" || v.tagName === "STRONG";
const isItalic = v => v.tagName === "I" || v.tagName === "EM";
const isUnderline = v => v.tagName === "U"; // wrap/unwrap the current selection in the given tag (bold/italic/underline). Copied from the
// Create Discussion composer so formatting behaves identically.
function toggle(root, has, query, tagName) {
    if (!root)
        return;
    const sel = document.getSelection();
    if (!sel)
        return;
    const hasEl = selectionMatches(root, sel, has);
    if (hasEl) {
        const range = sel.getRangeAt(0);
        const n = range.startContainer;
        let parent = null;
        if (n instanceof HTMLElement) {
            parent = n;
        }
        else {
            parent = n.parentElement;
        }
        if (!parent)
            return;
        if (has(parent)) {
            const contents = parent.childNodes;
            parent.replaceWith(...Array.from(contents));
            return;
        }
        Array.from(root.querySelectorAll(query)).filter(v => range.intersectsNode(v)).forEach(b => { const contents = b.childNodes; b.replaceWith(...Array.from(contents)); });
    }
    else {
        const range = sel.getRangeAt(0);
        const parent = range.startContainer.parentElement;
        if (!parent)
            return;
        const b = document.createElement(tagName);
        b.appendChild(range.extractContents());
        range.insertNode(b); // remove nested matches to avoid double-formatting.
        let toRemove = b.querySelector(query);
        while (toRemove) {
            toRemove.replaceWith(...Array.from(toRemove.childNodes));
            toRemove = b.querySelector(query);
        }
        sel.removeAllRanges();
        const r = new Range();
        r.selectNodeContents(b);
        sel.addRange(r);
    }
}
function selectionMatches(root, sel, matcher) { if (!root)
    return null; if (!sel)
    return null; let parent = null; if (sel.rangeCount === 0) {
    if (!sel.focusNode)
        return null;
    if (sel.focusNode instanceof HTMLElement) {
        parent = sel.focusNode;
    }
    else {
        parent = sel.focusNode.parentElement;
    }
}
else {
    const range = sel.getRangeAt(0);
    if (range.startContainer === range.endContainer) {
        if (!(range.startContainer instanceof HTMLElement)) {
            parent = range.startContainer.parentElement;
        }
        else {
            parent = range.startContainer;
        }
    }
    else {
        const n = range.commonAncestorContainer;
        if (n instanceof HTMLElement) {
            parent = n;
        }
        else {
            parent = n.parentElement;
        }
    }
} if (!parent)
    return null; while (parent && parent !== root) {
    if (matcher(parent))
        return parent;
    parent = parent.parentElement;
} return null; }
